/* eslint-disable eqeqeq */
/* eslint-disable no-use-before-define */
import { useCallback, useState } from 'react';
import { Badge, Button, Card, Form, InputGroup, ProgressBar, Table } from 'react-bootstrap';
import Swal from 'sweetalert2';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import UrlService from '../../services/UrlService';
import LoaderSubmit from '../LoaderSubmit';

export default function CardItem({ group, onUpdated }) {
    const axiosPrivate = useAxiosPrivate();
    const { code_no, cnt, items } = group;

    // edits: id -> new_code (initially current code_no)
    const [edits, setEdits] = useState(() => {
        const m = {};
        items.forEach((it) => {
            m[it.id] = it.code_no;
        });
        return m;
    });
    const [checkStatus, setCheckStatus] = useState({}); // id -> {unique, loading}
    const [autoLoading, setAutoLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [progress, setProgress] = useState(0);

    const handleAutoGenerate = async () => {
        setAutoLoading(true);
        try {
            const res = await axiosPrivate.post(UrlService.previewDuplicateUidUrl(), { code_no });
            if (res?.data?.status === 'success') {
                const previews = res.data.previews.filter((p) => p.group === code_no);
                const nextEdits = { ...edits };
                previews.forEach((p) => {
                    nextEdits[p.id] = p.new_code;
                });
                setEdits(nextEdits);
                // trigger check for each changed
                previews.filter((p) => !p.keep).forEach((p) => checkUniqueness(p.id, p.new_code));
            }
        } catch (err) {
            console.log(err);
        } finally {
            setAutoLoading(false);
        }
    };

    const checkUniqueness = useCallback(async (id, code) => {
        const trimmed = String(code || '').trim();
        if (!trimmed) {
            setCheckStatus((prev) => ({ ...prev, [id]: { unique: false, suggested: null, loading: false } }));
            return;
        }
        // 1) Intra-card duplicate check (same card edits)
        const normalized = trimmed.toUpperCase();
        const dupInCard = Object.entries(edits).some(([otherId, otherCode]) => parseInt(otherId, 10) !== parseInt(id, 10) && String(otherCode || '').trim().toUpperCase() === normalized);
        if (dupInCard) {
            // local duplicate — don't call server, show red cross immediately
            // ask server for a suggestion that is globally unique
            try {
                const res = await axiosPrivate.post(UrlService.checkDuplicateUidUrl(), { id, code_no: trimmed });
                let suggested = res?.data?.suggested || null;
                // ensure suggested not also duplicated inside this card
                if (suggested) {
                    const otherCodes = Object.entries(edits).filter(([oid]) => parseInt(oid, 10) !== parseInt(id, 10)).map(([, c]) => String(c || '').trim().toUpperCase());
                    let tries = 0;
                    while (otherCodes.includes(String(suggested).trim().toUpperCase()) && tries < 20) {
                        // bump numeric suffix
                        const prefix = String(suggested).slice(0, 3);
                        const num = parseInt(String(suggested).slice(3), 10) || 0;
                        suggested = prefix + (num + 1 + tries);
                        tries += 1;
                    }
                }
                setCheckStatus((prev) => ({ ...prev, [id]: { unique: false, suggested, loading: false, reason: 'Duplicate inside this card' } }));
            } catch {
                setCheckStatus((prev) => ({ ...prev, [id]: { unique: false, suggested: null, loading: false, reason: 'Duplicate inside this card' } }));
            }
            return;
        }

        setCheckStatus((prev) => ({ ...prev, [id]: { unique: null, loading: true } }));
        try {
            const res = await axiosPrivate.post(UrlService.checkDuplicateUidUrl(), { id, code_no: trimmed });
            if (res?.data?.status === 'success') {
                // if server says unique but we already know it's not duplicate in card, still need to ensure suggested not colliding
                const { unique } = res.data;
                let { suggested } = res.data;
                if (unique === false && suggested) {
                    const otherCodes = Object.entries(edits).filter(([oid]) => parseInt(oid, 10) !== parseInt(id, 10)).map(([, c]) => String(c || '').trim().toUpperCase());
                    let tries = 0;
                    while (otherCodes.includes(String(suggested).trim().toUpperCase()) && tries < 20) {
                        const prefix = String(suggested).slice(0, 3);
                        const num = parseInt(String(suggested).slice(3), 10) || 0;
                        suggested = prefix + (num + 1);
                        tries += 1;
                    }
                }
                setCheckStatus((prev) => ({ ...prev, [id]: { unique, suggested, loading: false } }));
            }
        } catch (err) {
            setCheckStatus((prev) => ({ ...prev, [id]: { unique: false, loading: false } }));
        }
    }, [axiosPrivate, edits]);

    const handleChange = (id, value) => {
        setEdits((prev) => ({ ...prev, [id]: value }));
        // debounce check
        clearTimeout(window[`check_${id}`]);
        window[`check_${id}`] = setTimeout(() => checkUniqueness(id, value), 600);
    };

    const handleUpdate = async () => {
        // only send changed — keep untouched not sent, fixes false duplicate on keep (EQU234 case)
        const updates = items
            .filter((it) => String(edits[it.id] || '').trim() !== String(it.code_no || '').trim())
            .map((it) => ({ id: it.id, code_no: String(edits[it.id]).trim() }));
        if (updates.length === 0) {
            Swal.fire({ icon: 'info', title: 'Nothing changed', text: 'No code was modified in this card.' });
            return;
        }
        setUpdating(true);
        setProgress(20);
        try {
            // fake progress tick
            const tick = setInterval(() => setProgress((p) => (p < 90 ? p + 10 : p)), 300);
            const res = await axiosPrivate.post(UrlService.updateDuplicateUidUrl(), { updates });
            clearInterval(tick);
            setProgress(100);

            if (res?.data?.status === 'success') {
                setProgress(100);
                await Swal.fire({
                    icon: 'success',
                    title: 'All unique & stored!',
                    html: `<div style="text-align:left"><p><b>${code_no}</b> group — <b>${res.data.success.length}</b> codes updated.</p>
                           <ul>${res.data.success.map((s) => `<li>ID ${s.id}: <code>${s.old_code}</code> → <b>${s.new_code}</b></li>`).join('')}</ul>
                           <p style="color:#198754">✨ Creative success — duplicates fixed!</p></div>`,
                    confirmButtonColor: '#198754',
                    confirmButtonText: 'Great!',
                });
                if (onUpdated) onUpdated();
            } else if (res?.data?.status === 'partial') {
                const failed = res.data.failed || [];
                // show first failed with suggestion
                const f = failed[0];
                const suggestedHtml = f.suggested
                    ? `<p>You can use this: <b style="color:#0d6efd;cursor:pointer" id="suggest-${f.id}">${f.suggested}</b> — click to fill</p>`
                    : '';
                const result = await Swal.fire({
                    icon: 'warning',
                    title: `Duplicate: ID ${f.id}`,
                    html: `<div style="text-align:left"><p>Code <code>${f.code_no}</code> duplicate — ${f.reason}.</p>${suggestedHtml}<p class="text-muted small">Tried ${updates.length}, success ${res.data.success.length}, failed ${failed.length}</p>
                           <ul>${failed.map((x) => `<li>ID ${x.id}: ${x.code_no} — ${x.reason}</li>`).join('')}</ul></div>`,
                    confirmButtonText: 'Use suggested & retry',
                    showCancelButton: true,
                    cancelButtonText: 'Close',
                    didOpen: () => {
                        const el = document.getElementById(`suggest-${f.id}`);
                        if (el) el.addEventListener('click', () => {
                            setEdits((prev) => ({ ...prev, [f.id]: f.suggested }));
                            checkUniqueness(f.id, f.suggested);
                            Swal.close();
                        });
                    },
                });
                if (result.isConfirmed && f.suggested) {
                    setEdits((prev) => ({ ...prev, [f.id]: f.suggested }));
                    checkUniqueness(f.id, f.suggested);
                }
                if (res.data.success.length > 0 && onUpdated) onUpdated(); // refresh to remove fixed groups partially
            } else {
                // full error
                const failed = res?.data?.failed || [];
                const f = failed[0];
                await Swal.fire({
                    icon: 'error',
                    title: 'Update failed — duplicate',
                    html: `<div style="text-align:left"><p>ID <b>${f?.id}</b> code <code>${f?.code_no}</code>: ${f?.reason}</p>
                           ${f?.suggested ? `<p>You can use this: <b style="color:#0d6efd;cursor:pointer" id="suggest2-${f.id}">${f.suggested}</b></p>` : ''}</div>`,
                    confirmButtonText: 'OK',
                    didOpen: () => {
                        const el = document.getElementById(`suggest2-${f?.id}`);
                        if (el) el.addEventListener('click', () => {
                            setEdits((prev) => ({ ...prev, [f.id]: f.suggested }));
                            checkUniqueness(f.id, f.suggested);
                            Swal.close();
                        });
                    },
                });
            }
        } catch (err) {
            const data = err?.response?.data;
            const failed = data?.failed || [];
            if (failed.length) {
                const f = failed[0];
                await Swal.fire({
                    icon: 'error',
                    title: `Duplicate: ID ${f.id}`,
                    html: `<p><code>${f.code_no}</code> — ${f.reason}</p>${f.suggested ? `<p>You can use this: <b>${f.suggested}</b> <button id="suggBtn" class="btn btn-sm btn-primary">Use</button></p>` : ''}`,
                    didOpen: () => {
                        const btn = document.getElementById('suggBtn');
                        if (btn) btn.addEventListener('click', () => {
                            setEdits((prev) => ({ ...prev, [f.id]: f.suggested }));
                            Swal.close();
                        });
                    },
                });
            } else {
                Swal.fire({ icon: 'error', title: 'Update failed', text: err?.response?.data?.message || err?.message });
            }
        } finally {
            setUpdating(false);
            setTimeout(() => setProgress(0), 1200);
        }
    };

    // keep for future use (allUnique/hasChecked)

    return (
        <Card className="mb-4 shadow border-0 overflow-hidden" style={{ borderRadius: '12px', background: '#ffffff', border: '1px solid #e9ecef' }}>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2 py-3" style={{ background: '#f0f4ff', borderBottom: '1px solid #d6e0ff' }}>
                <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-primary px-3 py-2" style={{ fontSize: '0.9rem', borderRadius: '8px' }}>{code_no}</span>
                    <Badge bg="light" text="dark" className="border px-2 py-2">{cnt} duplicates</Badge>
                    <small className="text-muted ms-1 d-none d-sm-inline">— 1st keep, rest unique</small>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Button variant="outline-primary" size="sm" onClick={handleAutoGenerate} disabled={autoLoading || updating} title={`Auto-generate for ${code_no}`}>
                        {autoLoading ? 'Generating...' : 'Auto-generate unique'}
                    </Button>
                    {autoLoading && <LoaderSubmit />}
                    <Button variant="primary" size="sm" onClick={handleUpdate} disabled={updating} title={`Update ${code_no}`}>
                        {updating ? 'Updating...' : 'Code update'}
                    </Button>
                </div>
            </Card.Header>
            {updating && <ProgressBar animated now={progress} label={`${progress}%`} className="rounded-0" style={{ height: '5px' }} />}
            <Card.Body className="p-0" style={{ background: '#fbfcfe' }}>
                <Table bordered size="sm" className="mb-0" style={{ background: '#ffffff' }}>
                    <thead className="table-light">
                        <tr>
                            <th width="60">ID</th>
                            <th>UID</th>
                            <th>Name</th>
                            <th>Category</th>
                            <th>Generic</th>
                            <th width="170">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it, idx) => (
                            <tr key={it.id} className={idx === 0 ? 'table-success' : ''}>
                                <td>{it.id} {idx === 0 && <Badge bg="success">keep</Badge>}</td>
                                <td>
                                    <InputGroup size="sm">
                                        <Form.Control
                                            value={edits[it.id] || ''}
                                            onChange={(e) => handleChange(it.id, e.target.value)}
                                            disabled={updating}
                                            isInvalid={checkStatus[it.id]?.unique === false}
                                            isValid={checkStatus[it.id]?.unique === true}
                                        />
                                        <InputGroup.Text>
                                            {checkStatus[it.id]?.loading ? '...' : checkStatus[it.id]?.unique === true ? <span style={{ color: 'green' }}>✓</span> : checkStatus[it.id]?.unique === false ? <span style={{ color: 'red' }}>✕</span> : <span className="text-muted">—</span>}
                                        </InputGroup.Text>
                                    </InputGroup>
                                    {checkStatus[it.id]?.unique === false && checkStatus[it.id]?.suggested && (
                                        <small role="button" tabIndex={0} className="text-primary" style={{ cursor: 'pointer' }} onClick={() => { setEdits((prev) => ({ ...prev, [it.id]: checkStatus[it.id].suggested })); checkUniqueness(it.id, checkStatus[it.id].suggested); }} onKeyDown={(e) => e.key === 'Enter' && (() => { setEdits((prev) => ({ ...prev, [it.id]: checkStatus[it.id].suggested })); checkUniqueness(it.id, checkStatus[it.id].suggested); })()}>
                                            you can use this: <b>{checkStatus[it.id].suggested}</b>
                                        </small>
                                    )}
                                </td>
                                <td>{it.name}</td>
                                <td>{it.category_name}</td>
                                <td>{it.generic_name}</td>
                                <td>
                                    {idx === 0 ? <Badge bg="success">1st item keep</Badge> : checkStatus[it.id]?.unique === true ? <Badge bg="success">unique ✓</Badge> : checkStatus[it.id]?.unique === false ? <Badge bg="danger">duplicate ✕</Badge> : <Badge bg="secondary">not checked</Badge>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Card.Body>
        </Card>
    );
}
